// src/pages/portal/PortalDashboard.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { usePortalAuth } from '../../context/PortalAuthContext';
import PortalLayout from '../../components/PortalLayout';

function StatusBadge({ status }) {
  const map = {
    draft:    { bg: '#f0f0ec', color: '#666',    label: 'Draft' },
    sent:     { bg: '#e8f0ff', color: '#2563eb', label: 'Sent' },
    approved: { bg: '#e8f7ee', color: '#16a34a', label: 'Approved' },
    declined: { bg: '#fdf0f0', color: '#dc2626', label: 'Declined' },
    paid:     { bg: '#e8f7ee', color: '#16a34a', label: 'Paid' },
    unpaid:   { bg: '#fff8e1', color: '#d97706', label: 'Unpaid' },
    overdue:  { bg: '#fdf0f0', color: '#dc2626', label: 'Overdue' },
    partial:  { bg: '#e8f0ff', color: '#2563eb', label: 'Partial' },
  };
  const s = map[status?.toLowerCase()] || { bg: '#f0f0ec', color: '#666', label: status };
  return (
    <span style={{ background: s.bg, color: s.color, padding: '3px 9px', borderRadius: '12px', fontSize: '11.5px', fontWeight: '600', letterSpacing: '0.02em' }}>
      {s.label}
    </span>
  );
}

function fmt(amount) {
  if (amount == null || amount === 0) return '—';
  return '$' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function PortalDashboard() {
  const { clientRecord } = usePortalAuth();
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientRecord?.id) return;
    async function load() {
      const [estRes, invRes] = await Promise.all([
        supabase
          .from('estimates')
          .select('id, title, quote_number, suggested_price, status, created_at, expiry_date')
          .eq('client_id', clientRecord.id)
          .in('status', ['sent', 'approved', 'declined'])
          .order('created_at', { ascending: false })
          .limit(5),
        supabase
          .from('invoices')
          .select('id, title, invoice_number, amount, total, status, due_date, created_at')
          .eq('client_id', clientRecord.id)
          .order('created_at', { ascending: false })
          .limit(5),
      ]);
      if (estRes.data) setEstimates(estRes.data);
      if (invRes.data) setInvoices(invRes.data);
      setLoading(false);
    }
    load();
  }, [clientRecord]);

  // Use total || amount for invoice amounts (handles both old and new records)
  const totalOwed = invoices
    .filter(i => ['unpaid', 'overdue', 'partial'].includes(i.status?.toLowerCase()))
    .reduce((sum, i) => sum + Number(i.total || i.amount || 0), 0);

  if (loading) return (
    <PortalLayout>
      <div style={{ color: '#999', fontSize: '14px', paddingTop: '40px', textAlign: 'center' }}>Loading your account…</div>
    </PortalLayout>
  );

  return (
    <PortalLayout>
      {/* Greeting */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#1a1a18', margin: '0 0 6px' }}>
          Welcome back{clientRecord?.name ? `, ${clientRecord.name.split(' ')[0]}` : ''}.
        </h1>
        <p style={{ fontSize: '14px', color: '#888', margin: 0 }}>
          Here's a summary of your account with TLC Unlimited.
        </p>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '40px' }}>
        {[
          { label: 'Open Invoices', value: invoices.filter(i => ['unpaid','overdue','partial'].includes(i.status?.toLowerCase())).length, color: '#d97706' },
          { label: 'Amount Due', value: fmt(totalOwed), color: '#dc2626' },
          { label: 'Pending Estimates', value: estimates.filter(e => ['sent'].includes(e.status?.toLowerCase())).length, color: '#2563eb' },
          { label: 'Total Invoices', value: invoices.length, color: '#1a1a18' },
        ].map(card => (
          <div key={card.label} style={{
            background: '#fff', border: '1px solid #e8e4dc',
            borderLeft: `4px solid ${card.color}`,
            borderRadius: '8px', padding: '18px 20px',
          }}>
            <div style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a18' }}>{card.value}</div>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', letterSpacing: '0.02em' }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Recent Estimates */}
      <Section title="Recent Estimates" link="/portal/estimates" linkLabel="View all">
        {estimates.length === 0 ? (
          <Empty text="No estimates on file yet." />
        ) : estimates.map(e => (
          <Row key={e.id}
            left={<>
              <div style={rowTitle}>{e.title || (e.quote_number ? `Quote #${e.quote_number}` : `Estimate`)}</div>
              <div style={rowSub}>{fmtDate(e.created_at)}</div>
            </>}
            right={<>
              <StatusBadge status={e.status} />
              {/* Use suggested_price — this is the client-facing price stored on estimate */}
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a18', marginLeft: '12px' }}>
                {fmt(e.suggested_price)}
              </div>
              <Link to={`/portal/estimates/${e.id}`} style={viewLink}>View →</Link>
            </>}
          />
        ))}
      </Section>

      {/* Recent Invoices */}
      <Section title="Recent Invoices" link="/portal/invoices" linkLabel="View all">
        {invoices.length === 0 ? (
          <Empty text="No invoices on file yet." />
        ) : invoices.map(inv => (
          <Row key={inv.id}
            left={<>
              <div style={rowTitle}>{inv.title || `Invoice #${inv.invoice_number || inv.id?.slice(0,6)}`}</div>
              <div style={rowSub}>Due {fmtDate(inv.due_date)}</div>
            </>}
            right={<>
              <StatusBadge status={inv.status} />
              {/* Use total || amount — handles both old and new invoice records */}
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a18', marginLeft: '12px' }}>
                {fmt(inv.total || inv.amount)}
              </div>
              <Link to={`/portal/invoices/${inv.id}`} style={viewLink}>View →</Link>
            </>}
          />
        ))}
      </Section>
    </PortalLayout>
  );
}

function Section({ title, link, linkLabel, children }) {
  return (
    <div style={{ marginBottom: '36px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h2 style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a18', margin: 0, letterSpacing: '-0.01em' }}>{title}</h2>
        <Link to={link} style={{ fontSize: '12.5px', color: '#c8a96e', textDecoration: 'none', fontWeight: '500' }}>{linkLabel}</Link>
      </div>
      <div style={{ background: '#fff', border: '1px solid #e8e4dc', borderRadius: '8px', overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
}

function Row({ left, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #f0ece4' }}>
      <div>{left}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{right}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div style={{ padding: '24px 18px', fontSize: '13px', color: '#aaa' }}>{text}</div>;
}

const rowTitle = { fontSize: '14px', fontWeight: '500', color: '#1a1a18' };
const rowSub   = { fontSize: '12px', color: '#aaa', marginTop: '2px' };
const viewLink = { fontSize: '12.5px', color: '#c8a96e', textDecoration: 'none', marginLeft: '12px', fontWeight: '500', whiteSpace: 'nowrap' };
